import {Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import {
  AmountStatus,
  ApplicationFormSettings,
  Competitor,
  CompetitorRegistrationDto,
  Country,
  EventItem,
  FieldConfig,
  Lookups, PaymentStatus,
  Race,
  RaceRegistration,
  RaceSettings, RegisterHelper, RegisterToRaceUserType, RegistrationStatus,
  TeamType
} from "@model";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {AbstractComponent, CompetitorTableType} from "@components";
import {
  RegisterToRaceItemComponent
} from "../../../../../public/register-to-race/register-to-race-item/register-to-race-item.component";
import {required} from "@utility";

@Component({
  selector: 'app-team-members-management-form',
  templateUrl: './team-members-management-form.component.html',
  styleUrl: './team-members-management-form.component.scss'
})
export class TeamMembersManagementFormComponent extends AbstractComponent implements OnInit {


  @Input() race: Race | undefined;
  @Input() event: EventItem | undefined;
  @Input() teamMemberEdit: boolean = false;
  @Input() competitor: Competitor | undefined;
  @Input() raceSettings: RaceSettings | undefined;
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Input() raceRegistration: RaceRegistration | undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() title: string | undefined;
  @Input() openInModal: boolean = false;
  @Output() onChangeEmail: EventEmitter<string> = new EventEmitter<string>();
  @Output() onSave: EventEmitter<Competitor> = new EventEmitter<Competitor>();
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();
  @Input() lookups: LookupTeamMemberLists = new LookupTeamMemberLists();
  @Input() assignStartNumber: boolean = false;
  @Input() index: number = 0;


  inputForm = new FormGroup({
    raceName: new FormControl(),
    teamName: new FormControl(),
    id: new FormControl(),
    startNumber: new FormControl(),
    startBlock: new FormControl(),
    orderNumber: new FormControl(),
    paymentStatus: new FormControl(),
    paymentStatusOrganiser: new FormControl(),
    paymentStatusTracey: new FormControl(),
    remainingAmountOrganiser: new FormControl(),
    remainingAmountTracey: new FormControl(),
    paymentComment: new FormControl(),
  });

  customFields: FieldConfig[] = [];
  isTeam: boolean = false;
  @Input() registerHelper: RegisterHelper | undefined;
  competitorRegistration: CompetitorRegistrationDto = new CompetitorRegistrationDto();
  @ViewChildren(RegisterToRaceItemComponent) registerToRaceComponentItems!: QueryList<RegisterToRaceItemComponent>;

  constructor(public formBuilder: FormBuilder) {
    super();
  }

  ngOnInit() {
    if (this.competitor) {
      this.competitorRegistration = Competitor.competitorToDto(this.competitor);
    }
    if (!!this.raceSettings?.teamType && this.raceSettings.teamType !== TeamType.SINGLE && !this.teamMemberEdit) {
      this.isTeam = true;
      this.inputForm.controls.teamName.setValue(this.competitor?.lastName);
      this.inputForm.controls.teamName.addValidators([required]);
    }
    if (!this.teamMemberEdit) {
      this.inputForm.controls.raceName.setValue(this.race?.name ?? "");
      this.inputForm.controls.raceName.disable();
      this.inputForm.controls.id.setValue(this.competitor?.id);
      this.inputForm.controls.id.disable();
      this.inputForm.controls.startNumber.setValue(this.competitor?.startNumber);
      if (this.competitor?.startTime) {
        this.inputForm.controls.startNumber.addValidators([required]);
      }
      if (this.raceSettings?.blockStart) {
        this.inputForm.controls.startBlock.setValue(this.competitor?.startBlock);
        if (this.competitor?.startTime) {
          this.inputForm.controls.startBlock.addValidators([required]);
        }
      } else {
        this.inputForm.controls.startBlock.disable()
      }
      if (this.raceRegistration) {
        this.inputForm.controls.orderNumber.setValue(this.raceRegistration?.order?.id);
      }
      this.inputForm.controls.paymentStatus.setValue(RaceRegistration.getAmountStatus(this.raceRegistration));
      this.inputForm.controls.paymentStatus.disable();
      this.inputForm.controls.paymentComment.setValue(this.competitor?.fee);
      this.inputForm.controls.orderNumber.disable();
    }
  }

  onEmailChange(email: string) {
    this.onChangeEmail.emit(email)
  }


  isValid(): boolean {
    let valid: boolean = true;
    if (this.registerToRaceComponentItems.get(0)) {
      let registerToRaceItem: RegisterToRaceItemComponent | undefined = this.registerToRaceComponentItems.get(0);
      registerToRaceItem?.markAll();
      valid = !!registerToRaceItem?.inputForm.valid;
    }
    if (!this.teamMemberEdit) {
      this.markAllAsTouched(this.inputForm);
    }
    return (this.teamMemberEdit || this.inputForm.valid) && valid
  }

  getData(): { competitor: Competitor, raceRegistration: RaceRegistration } | undefined {
    const competitorCopy: Competitor = {...this.competitor};
    const raceRegistrationCopy: RaceRegistration = {...this.raceRegistration};
    if (this.registerToRaceComponentItems.get(0)) {
      let registerToRaceItem: RegisterToRaceItemComponent | undefined = this.registerToRaceComponentItems.get(0);
      if (!!registerToRaceItem) {
        registerToRaceItem.onGetDataFields();
        const competitorDTO: CompetitorRegistrationDto = {...registerToRaceItem?.competitorRegistration}
        competitorCopy.firstName = competitorDTO.firstName;
        competitorCopy.lastName = competitorDTO.lastName;
        competitorCopy.nickname = competitorDTO.nickname
        competitorCopy.sex = competitorDTO.gender;
        competitorCopy.birthday = competitorDTO.birthday;
        competitorCopy.address = competitorDTO.address;
        competitorCopy.placeOfResidence = competitorDTO.city;
        competitorCopy.country = competitorDTO.country;
        competitorCopy.identityCardNumber = competitorDTO.identityCardNumber;
        competitorCopy.identityCardIssuer = competitorDTO.identityCardIssuer;
        competitorCopy.identityCardExpires = competitorDTO.identityExpiration;
        competitorCopy.club = competitorDTO.club;
        competitorCopy.mobilePhone = competitorDTO.mobilePhone;
        competitorCopy.emergencyPhone = competitorDTO.emergencyPhone;
        competitorCopy.email = competitorDTO.email;
        competitorCopy.tshirt = competitorDTO.tShirt;
        competitorCopy.meal = competitorDTO.meal;
        competitorCopy.transport = competitorDTO.transport;
        competitorCopy.bestTime = competitorDTO.bestTime;
        competitorCopy.licence = competitorDTO.licence;
        competitorCopy.comment = competitorDTO.comment;
        competitorCopy.chipNumber = competitorDTO.chipNumber;
        competitorCopy.customFieldValues = competitorDTO.customFieldValues;
        competitorCopy.nationality = competitorDTO.nationality;
        competitorCopy.discipline = competitorDTO.discipline;
      }
    }
    if (!this.teamMemberEdit) {
      competitorCopy.startNumber = this.inputForm.controls.startNumber.value;
      if (!competitorCopy.startNumber || competitorCopy.startNumber.trim().length === 0) {
        competitorCopy.startNumber = undefined;
      }
      if (this.raceSettings?.blockStart) {
        competitorCopy.startBlock = this.inputForm.controls.startBlock.value;
      }
      if (this.isTeam) {
        competitorCopy.lastName = this.inputForm.controls.teamName.value;
        competitorCopy.fullName = competitorCopy.firstName + " " + competitorCopy.lastName;
      }

      // TODO set paymentStatus, paymentStatusOrganiser, paymentStatusTracey, remainingAmountOrganiser
      // remainingAmountTracey, paymentComment
      competitorCopy.fee = this.inputForm.controls.paymentComment.value;
    }
    return this.isValid() ? {competitor: competitorCopy, raceRegistration: raceRegistrationCopy} : undefined;
  }


  actionButtonSave() {
    this.onSave.emit();
  }

  actionButtonCancel() {
    this.onCancel.emit();
  }


  protected readonly TeamType = TeamType;
  protected readonly CompetitorTableType = CompetitorTableType;
  protected readonly RegisterToRaceUserType = RegisterToRaceUserType;

}


export class LookupTeamMemberLists {
  countries: Country[] = [];
  tshirtSizes: Lookups[] = [];
  disciplines: Lookups[] = [];
  meals: Lookups[] = [];
  genders: Lookups[] = [];
  transports: Lookups[] = [];
  currencies: Lookups[] = [];

  constructor() {
  }
}
